import { useEffect } from 'react';
import type { Json } from './schemas';

const SITE_URL = 'https://www.recovry.in';
// Fallback link-preview image for every page that doesn't override it -
// centre pages, service and city pages, knowledge, etc. Points at the
// patient-facing campaign card; partner pages override with their own.
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-users.png`;

/**
 * Campaign link-preview images (1200x630, <300KB). Absolute URLs on the
 * canonical www host on purpose - scrapers, WhatsApp especially, do not
 * reliably follow a redirect when fetching og:image, so the bare
 * recovry.in form would cost a hop and can silently fail.
 */
export const OG_IMAGE_USERS = `${SITE_URL}/og-users.png`;
/** Home page only. Google shrinks the result thumbnail to a small square, and
 *  the campaign card's wordmark sat in its left third, so it became a smudge.
 *  This one keeps the wordmark large and centred, inside the crop-safe middle. */
export const OG_IMAGE_HOME = `${SITE_URL}/og-home.png`;
export const OG_IMAGE_PARTNERS = `${SITE_URL}/og-partners.png`;

const MARKER = 'data-seo-managed';

export interface SeoProps {
  title: string;
  description: string;
  /** Path-only canonical (e.g. "/physiotherapy-in-noida-sector-18"). Will be prefixed with site URL. */
  canonical: string;
  /** Defaults to "index, follow". Pass "noindex" for thin/empty pages. */
  robots?: string;
  /** Defaults to title. */
  ogTitle?: string;
  /** Defaults to description. */
  ogDescription?: string;
  /** Absolute URL of the OG image. */
  ogImage?: string;
  ogType?: 'website' | 'article';
  /** Page-level structured data - array of plain objects from `schemas.ts` builders. */
  schema?: Json[];
  /** Optional keywords meta. Skip unless intentionally targeting a tight cluster. */
  keywords?: string;
}

/**
 * Per-page head + JSON-LD wrapper. Manages document.head imperatively
 * with proper cleanup on unmount - avoids react-helmet-async's stale-tag
 * issues with React 19 concurrent rendering.
 *
 * On mount: removes any prior SEO-managed tags, then sets new ones.
 * On unmount: removes everything it added (so route changes are clean).
 *
 * Each managed element carries `data-seo-managed="true"` so multiple
 * <Seo> instances and the prerender DOM dedup can identify them.
 */
export function Seo({
  title,
  description,
  canonical,
  robots = 'index, follow, max-snippet:-1, max-image-preview:large',
  ogTitle,
  ogDescription,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  schema,
  keywords,
}: SeoProps) {
  useEffect(() => {
    const fullCanonical = canonical.startsWith('http') ? canonical : `${SITE_URL}${canonical}`;
    const finalOgTitle = ogTitle ?? title;
    const finalOgDescription = ogDescription ?? description;

    // Sweep all previously-managed SEO tags so we start from a clean slate.
    document.querySelectorAll(`[${MARKER}]`).forEach((el) => el.remove());

    const head = document.head;
    const created: Element[] = [];

    // <title> - replace any existing default title rather than duplicate.
    const existingTitle = head.querySelector('title');
    if (existingTitle) existingTitle.remove();
    const titleEl = document.createElement('title');
    titleEl.setAttribute(MARKER, 'true');
    titleEl.textContent = title;
    head.appendChild(titleEl);
    created.push(titleEl);
    document.title = title;

    const meta = (attr: 'name' | 'property', key: string, content: string) => {
      // Remove any existing tag with this key — including the static defaults in
      // index.html (which crawlers rely on) — so the client never ends up with
      // duplicate og:/twitter: tags, only the up-to-date managed one.
      head.querySelectorAll(`meta[${attr}="${CSS.escape(key)}"]`).forEach((el) => el.remove());
      const el = document.createElement('meta');
      el.setAttribute(attr, key);
      el.setAttribute('content', content);
      el.setAttribute(MARKER, 'true');
      head.appendChild(el);
      created.push(el);
    };

    meta('name', 'description', description);
    if (keywords) meta('name', 'keywords', keywords);
    meta('name', 'robots', robots);

    meta('property', 'og:type', ogType);
    meta('property', 'og:title', finalOgTitle);
    meta('property', 'og:description', finalOgDescription);
    meta('property', 'og:url', fullCanonical);
    meta('property', 'og:image', ogImage);
    meta('property', 'og:image:width', '1200');
    meta('property', 'og:image:height', '630');
    meta('property', 'og:image:alt', finalOgTitle);
    meta('property', 'og:locale', 'en_IN');
    meta('property', 'og:site_name', 'Recovry');

    meta('name', 'twitter:card', 'summary_large_image');
    meta('name', 'twitter:title', finalOgTitle);
    meta('name', 'twitter:description', finalOgDescription);
    meta('name', 'twitter:image', ogImage);

    // canonical - replace any existing.
    const existingCanonical = head.querySelector('link[rel="canonical"]');
    if (existingCanonical) existingCanonical.remove();
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', fullCanonical);
    link.setAttribute(MARKER, 'true');
    head.appendChild(link);
    created.push(link);

    // JSON-LD per-page schema blocks.
    if (schema) {
      for (const obj of schema) {
        const s = document.createElement('script');
        s.setAttribute('type', 'application/ld+json');
        s.setAttribute(MARKER, 'true');
        s.textContent = JSON.stringify(obj);
        head.appendChild(s);
        created.push(s);
      }
    }

    return () => {
      // Cleanup on unmount: remove only what this instance created.
      for (const el of created) el.remove();
    };
  }, [
    title,
    description,
    canonical,
    robots,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    keywords,
    // schema is an array of objects - JSON.stringify for stable comparison.
    JSON.stringify(schema),
  ]);

  return null;
}

/** Build a path-only canonical from the route the user is on. */
export function canonicalFor(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}
